import React, { useState, useCallback, useContext } from 'react';
import { Button } from 'semantic-ui-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { BluetoothIcon } from 'assets/icons/brikks/BluetoothIcon';
import { EmailIcon } from 'assets/icons/EmailIcon';
import { DiscardIcon } from 'assets/icons/DiscardIcon';
import { EyeIcon } from 'assets/icons/EyeIcon';
import { EyeIconClosed } from 'assets/icons/EyeIconClosed';
import { BluetoothSearchingIcon } from 'assets/icons/brikks/BluetoothSearchingIcon';
import { useGetPairingStatus } from 'services/2n/get-pairing-status';
import { useDeletePairing } from 'services/2n/delete-pairing';
import { useStopPairing } from 'services/2n/stop-pairing';
import { useStartPairing } from 'services/2n/start-pairing';
import { useSendPinForPairing } from 'services/2n/send-pin-for-pairing';
import { useGetUserIn2n } from 'services/2n/get-user';
import { useModal } from 'store/modals';
import { Text } from 'components/typography';
import { LabeledElement } from 'components/form-elements/labels/LabeledElement';
import { UserAdministrationModalContext } from '../../../../UserAdministrationModalContext';
import styles from './styles.module.scss';

export const FetchActivationCode = () => {

  const intl = useIntl();
  const [showCode, setShowCode] = useState(false);

  const { selectedUser } = useContext(UserAdministrationModalContext);

  const getPairingStatus = useGetPairingStatus(selectedUser);
  const deletePairingStatus = useDeletePairing(selectedUser.email);
  const getUserIn2n = useGetUserIn2n(selectedUser.email);
  const startPairing = useStartPairing(getUserIn2n.user);
  const stopPairing = useStopPairing(getUserIn2n.user);
  const sendPinForPairing = useSendPinForPairing(getUserIn2n.user);

  const handleToggleShow = useCallback(() => {
    setShowCode((s) => !s);
  }, []);

  const showWarningToAssignGroup = getUserIn2n.user?.assignedGroups.length === 0;
  const { openModal } = useModal();
  const handleFetchClick = useCallback(() => {
    if (showWarningToAssignGroup) {
      openModal({
        modalType: 'NotificationModal',
        modalProps: {
          type: 'warning',
          description: 'assignGroupWarning',
        },
      });
    } else {
      startPairing.mutateAsync();
    }
  }, [openModal, startPairing]);

  return (
    <div>
      {!getPairingStatus.isPairingInitiated && (
      <LabeledElement
        label="appConnection"
        description="appConnectionDescription"
      >
        <Button
          basic
          color="primary"
          type="button"
          disabled={getPairingStatus.isLoading}
          onClick={() => handleFetchClick()}
          loading={getPairingStatus.isLoading || stopPairing.isLoading || startPairing.isLoading}
        >
          <FormattedMessage id="fetchActivationCode" />
        </Button>
      </LabeledElement>
      )}

      {getPairingStatus.isPairingInitiated && !getPairingStatus.isPaired && (
        <div className={styles.pairingContainer}>
          {getPairingStatus.code && (
            <div className={styles.pairingInfo}>
              <BluetoothSearchingIcon />
              <div className={styles.pairingCode}>
                <Text styles={styles.pairingLabel}>
                  <FormattedMessage
                    id="pairingCode"
                    defaultMessage="Pairing code: {code}"
                    values={{
                      code: <span>{getPairingStatus.code}</span>,
                    }}
                  />
                </Text>

                <div className={styles.timeLeft}>
                  <FormattedMessage
                    id="pairingTimeLimit"
                    values={{
                      numberOfDays: <span>{getPairingStatus.validNumberOfDays}</span>,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
          <div className={styles.actionOnly}>
            <Button
              size="small"
              basic
              disabled={stopPairing.isLoading || sendPinForPairing.isLoading}
              onClick={() => {
                openModal({
                  modalType: 'ConfirmationModal',
                  modalProps: {
                    title: intl.formatMessage({ id: 'sendCodeToUserEmail' }),
                    content: intl.formatMessage({ id: 'sendCodeToUserEmailContent' }),
                    onConfirm: () => sendPinForPairing.mutate(),
                  },
                });
              }}
              aria-label="email-activation-code"
            >
              <EmailIcon
                size={25}
                className={styles.emailIcon}
              />
            </Button>
            <Button
              size="small"
              basic
              disabled={stopPairing.isLoading || sendPinForPairing.isLoading}
              onClick={() => {
                openModal({
                  modalType: 'ConfirmationModal',
                  modalProps: {
                    title: intl.formatMessage({ id: 'stopPairingProcess' }),
                    content: intl.formatMessage({ id: 'stopPairingProcessContent' }),
                    onConfirm: () => stopPairing.mutate(),
                  },
                });
              }}
              aria-label="stop-pairing"
            >
              <DiscardIcon size={25} />
            </Button>
          </div>

        </div>
      )}

      {getPairingStatus.isPairingInitiated && getPairingStatus.isPaired && (
        <div className={styles.pairingContainer}>
          <div className={styles.pairingInfo}>
            <BluetoothIcon />
            <div className={styles.pairingCode}>
              <Text styles={styles.pairingLabel}>
                My2N app: {showCode ? getPairingStatus.mobileKeyHash : '••••••••'}
              </Text>
            </div>
          </div>
          <div className={styles.actionOnly}>
            <Button
              basic
              type="button"
              onClick={handleToggleShow}
              loading={getPairingStatus.isLoading}
              aria-label={showCode ? 'hide-activation-code' : 'show-activation-code'}
            >
              {showCode ? <EyeIconClosed size={25} /> : <EyeIcon size={25} />}
            </Button>
            <Button
              basic
              type="button"
              onClick={() => {
                openModal({
                  modalType: 'ConfirmationModal',
                  modalProps: {
                    title: intl.formatMessage({ id: 'unPairDevice' }),
                    content: intl.formatMessage({ id: 'unPairDeviceContent' }),
                    onConfirm: () => deletePairingStatus.mutateAsync(),
                  },
                });
              }}
              aria-label="unpair"
            >
              <DiscardIcon size={25} />
            </Button>
          </div>
        </div>
      )}

    </div>
  );
};
